import {
  getClipTimelineDuration,
} from "./projectSchema";
import { VIDEO_EDITOR_TRACK_TYPE } from "./constants";

export const getPackedTrackId = ({ type, visualIndex }) =>
  `${type}-lane-${visualIndex}`;

export const getClipInterval = (clip) => {
  const start = Math.max(0, Number(clip?.position) || 0);
  return {
    start,
    end: start + getClipTimelineDuration({ clip }),
  };
};

export const intervalsOverlap = ({ left, right }) =>
  left.start < right.end && right.start < left.end;

export const countOverlappingClips = ({ clips, clip }) => {
  const target = getClipInterval(clip);
  return clips.reduce((count, other) => {
    if (!other || other.id === clip.id) {
      return count;
    }
    return intervalsOverlap({ left: target, right: getClipInterval(other) })
      ? count + 1
      : count;
  }, 1);
};

const compareClipsForPacking = (left, right) => {
  const leftStart = Number(left.position) || 0;
  const rightStart = Number(right.position) || 0;
  if (leftStart !== rightStart) {
    return leftStart - rightStart;
  }

  const durationDelta =
    getClipTimelineDuration({ clip: left }) -
    getClipTimelineDuration({ clip: right });
  if (durationDelta !== 0) {
    return durationDelta;
  }

  return String(left.id).localeCompare(String(right.id));
};

const laneFitsItem = ({ occupied, laneIndex, start, end }) => {
  const lane = occupied[laneIndex] || [];
  return lane.every(
    (interval) => interval.end <= start || interval.start >= end
  );
};

const placeItem = ({ occupied, laneIndex, item }) => {
  while (occupied.length <= laneIndex) {
    occupied.push([]);
  }
  occupied[laneIndex].push({
    clipId: item.clip.id,
    start: item.start,
    end: item.end,
  });
};

const findFirstFittingLane = ({ occupied, start, end }) => {
  for (let laneIndex = 0; laneIndex < occupied.length; laneIndex += 1) {
    if (laneFitsItem({ occupied, laneIndex, start, end })) {
      return laneIndex;
    }
  }
  return occupied.length;
};

export const packClipsIntoLanes = ({ clips, preferredByClipId = {} }) => {
  const items = (clips || [])
    .filter(Boolean)
    .map((clip) => {
      const interval = getClipInterval(clip);
      return {
        clip,
        start: interval.start,
        end: interval.end,
      };
    })
    .sort((left, right) => compareClipsForPacking(left.clip, right.clip));

  const occupied = [];
  const preferredItems = [];
  const otherItems = [];

  items.forEach((item) => {
    if (Number.isInteger(preferredByClipId[item.clip.id])) {
      preferredItems.push(item);
      return;
    }
    otherItems.push(item);
  });

  preferredItems.forEach((item) => {
    const overlappingCount = countOverlappingClips({
      clips: clips.filter(Boolean),
      clip: item.clip,
    });
    const preferred = Math.max(
      0,
      Math.min(preferredByClipId[item.clip.id], Math.max(overlappingCount - 1, 0))
    );

    if (laneFitsItem({ occupied, laneIndex: preferred, start: item.start, end: item.end })) {
      placeItem({ occupied, laneIndex: preferred, item });
      return;
    }

    const fallback = findFirstFittingLane({
      occupied,
      start: item.start,
      end: item.end,
    });
    placeItem({ occupied, laneIndex: fallback, item });
  });

  otherItems.forEach((item) => {
    const laneIndex = findFirstFittingLane({
      occupied,
      start: item.start,
      end: item.end,
    });
    placeItem({ occupied, laneIndex, item });
  });

  return occupied
    .map((lane) => ({
      clipIds: lane.map((interval) => interval.clipId),
    }))
    .filter((lane) => lane.clipIds.length);
};

const isInvertedPackingType = (type) =>
  type === VIDEO_EDITOR_TRACK_TYPE.IMAGE ||
  type === VIDEO_EDITOR_TRACK_TYPE.VIDEO;

export const packingLaneToVisualIndex = ({ type, packingLane, laneCount }) => {
  if (isInvertedPackingType(type)) {
    return Math.max(0, laneCount - 1 - packingLane);
  }
  return packingLane;
};

export const visualIndexToPackingLane = ({ type, visualIndex, laneCount }) => {
  if (isInvertedPackingType(type)) {
    return Math.max(0, laneCount - 1 - visualIndex);
  }
  return visualIndex;
};

export const applyPackedLanesToEdl = ({ edl, type, packedLanes }) => {
  const packed = packedLanes?.length ? packedLanes : [{ clipIds: [] }];
  const laneCount = packed.length;
  const nextTracksById = { ...edl.tracks.byId };
  const nextClipsById = { ...edl.clips.byId };
  const remainingOrder = (edl.tracks.order || []).filter(
    (trackId) => nextTracksById[trackId]?.type !== type
  );

  Object.keys(nextTracksById).forEach((trackId) => {
    if (nextTracksById[trackId]?.type === type) {
      delete nextTracksById[trackId];
    }
  });

  const visualTrackIds = [];
  packed.forEach((lane, packingLane) => {
    const visualIndex = packingLaneToVisualIndex({
      type,
      packingLane,
      laneCount,
    });
    const trackId = getPackedTrackId({ type, visualIndex });
    visualTrackIds[visualIndex] = trackId;
    nextTracksById[trackId] = {
      id: trackId,
      type,
      clipIds: [...lane.clipIds],
    };

    lane.clipIds.forEach((clipId) => {
      const clip = nextClipsById[clipId];
      if (!clip) {
        return;
      }

      nextClipsById[clipId] = {
        ...clip,
        trackId,
        layerIndex: packingLane,
      };
    });
  });

  let nextOrder = remainingOrder;
  if (type === VIDEO_EDITOR_TRACK_TYPE.IMAGE) {
    const videoIndex = remainingOrder.findIndex(
      (trackId) => nextTracksById[trackId]?.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO
    );
    const insertIndex = videoIndex >= 0 ? videoIndex : remainingOrder.length;
    nextOrder = [
      ...remainingOrder.slice(0, insertIndex),
      ...visualTrackIds.filter(Boolean),
      ...remainingOrder.slice(insertIndex),
    ];
  } else if (type === VIDEO_EDITOR_TRACK_TYPE.VIDEO) {
    const audioIndex = remainingOrder.findIndex(
      (trackId) => nextTracksById[trackId]?.type === VIDEO_EDITOR_TRACK_TYPE.AUDIO
    );
    const insertIndex = audioIndex >= 0 ? audioIndex : remainingOrder.length;
    nextOrder = [
      ...remainingOrder.slice(0, insertIndex),
      ...visualTrackIds.filter(Boolean),
      ...remainingOrder.slice(insertIndex),
    ];
  } else if (type === VIDEO_EDITOR_TRACK_TYPE.AUDIO) {
    nextOrder = [...remainingOrder, ...visualTrackIds.filter(Boolean)];
  } else {
    nextOrder = [...remainingOrder, ...visualTrackIds.filter(Boolean)];
  }

  return {
    ...edl,
    tracks: {
      byId: nextTracksById,
      order: nextOrder,
    },
    clips: {
      byId: nextClipsById,
    },
  };
};
